import { AxiosResponse } from 'axios';
import { ApiResponse, GameSession, Answer, HostInfo, GameSpaceInfo, UpgradeToProRequest, AddTokensRequest, CreateGameRequest, Question, LeaderboardEntry } from '../types';
import { apiClient } from './apiClient';

interface JoinGameResponse {
  session: GameSession;
  hostId?: string; // Added hostId field to identify the session host
}

export const gameService = {
  // GameSpace methods
  getUserGameSpaces: async (): Promise<ApiResponse<GameSpaceInfo[]>> => {
    try {
      const response = await apiClient.get('/api/game/spaces');
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error getting user game spaces:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve game spaces',
        errors: error.response?.data?.errors || [],
        data: [] as GameSpaceInfo[]
      };
    }
  },
  
  getGameSpace: async (id: number): Promise<ApiResponse<GameSpaceInfo>> => {
    try {
      const response = await apiClient.get(`/api/game/space/${id}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error(`Error getting game space ${id}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve game space',
        errors: error.response?.data?.errors || [],
        data: {} as GameSpaceInfo
      };
    }
  },
  
  getGameSession: async (sessionId: string): Promise<ApiResponse<GameSession>> => {
    try {
      const response = await apiClient.get(`/api/game/sessions/${sessionId}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error(`Error getting game session ${sessionId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve game session',
        errors: error.response?.data?.errors || [],
        data: {} as GameSession
      };
    }
  },

  getGameSessions: async (gameSpaceId: number): Promise<ApiResponse<GameSession[]>> => {
    try {
      const response = await apiClient.get(`/api/game/space/${gameSpaceId}/sessions`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error(`Error getting game sessions for game space ${gameSpaceId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve game sessions',
        errors: error.response?.data?.errors || [],
        data: [] as GameSession[]
      };
    }
  },
  
  getActiveGamesInSpace: async (gameSpaceId: number): Promise<ApiResponse<GameSession[]>> => {
    try {
      const response = await apiClient.get(`/api/game/space/${gameSpaceId}/sessions`);
      console.log('Active games response:', response.data);
      
      // Filter active sessions (Created, WaitingForPlayers or InProgress)
      const activeSessions = response.data
        .filter((session: any) => {
          // Check both possible field names for status
          const sessionStatus = session.statusName;
          
          // Handle both string and numeric status values
          return sessionStatus === 'Created' || 
                 sessionStatus === 'WaitingForPlayers' || 
                 sessionStatus === 'InProgress';
        })
        .map((session: any): GameSession => {
          
          return {
            id: session.id,
            gameCode: session.gameCode,
            title: session.title,
            description: session.description,
            gameType: session.gameType,
            createdAt: session.createdAt,
            isPublic: session.isPublic,
            isActive: session.isActive,
            maxPlayers: session.maxPlayers,
            questionCollectionId: session.questionCollectionId,
            gameSpaceId: session.gameSpaceId,
            hostId: session.hostId,
            hostName: session.hostName,
            questionCollectionTitle: session.questionCollectionTitle,
            hostUserId: session.hostUserId,
            totalQuestionCount: session.totalQuestionCount,
            currentQuestionIndex: session.currentQuestionIndex,
            players: session.players,
            leaderboard: session.leaderboard,
            status: session.status,
            statusName: session.statusName,
            startTime: session.startedAt || session.createdAt,
            endTime: session.endedAt,
            currentPlayerCount: session.playerCount || session.currentPlayers || 0,
          };
        });
      return { success: true, data: activeSessions };
    } catch (error: any) {
      console.error(`Error getting active games for space ${gameSpaceId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve active games',
        errors: error.response?.data?.errors || [],
        data: [] as GameSession[]
      };
    }
  },
  
  getPastGamesInSpace: async (gameSpaceId: number): Promise<ApiResponse<GameSession[]>> => {
    try {
      const response = await apiClient.get(`/api/game/space/${gameSpaceId}/sessions`);
      console.log('Past games response:', response.data);
      
      // Filter completed or cancelled sessions
      const pastSessions = response.data
        .filter((session: any) => {
          // Check both possible field names for status
          const sessionStatus = session.statusName;
          
          // Handle both string and numeric status values
          if (typeof sessionStatus === 'string') {
            return sessionStatus === 'Completed' || sessionStatus === 'Cancelled';
          } else if (typeof sessionStatus === 'number') {
            // Assuming numeric values: 0=Created, 1=WaitingForPlayers, 2=InProgress, 3=Completed, 4=Cancelled
            return sessionStatus === 3 || sessionStatus === 4;
          }
          return false;
        })
        .map((session: any): GameSession =>  {
          
          return {
            gameType: session.gameType,
            createdAt: session.createdAt,
            isPublic: session.isPublic,
            isActive: session.isActive,
            maxPlayers: session.maxPlayers,
            questionCollectionId: session.questionCollectionId,
            gameSpaceId: session.gameSpaceId,
            hostId: session.hostId,
            hostName: session.hostName,
            questionCollectionTitle: session.questionCollectionTitle,
            hostUserId: session.hostUserId,
            id: session.id,
            gameCode: session.gameCode,
            title: session.title,
            description: session.description,
            totalQuestionCount: session.totalQuestionCount,
            currentQuestionIndex: session.currentQuestionIndex,
            players: session.players,
            leaderboard: session.leaderboard,
            // Use the normalized status
            status: session.status,
            statusName: session.statusName,
            startTime: session.startedAt || session.createdAt,
            endTime: session.endedAt,
            currentPlayerCount: session.playerCount || session.currentPlayers || 0,
          };
        });
      return { success: true, data: pastSessions };
    } catch (error: any) {
      console.error(`Error getting past games for space ${gameSpaceId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve past games',
        errors: error.response?.data?.errors || [],
        data: [] as GameSession[]
      };
    }
  },
  async getPublicGames(): Promise<ApiResponse<GameSession[]>> {
    try {
      const response: AxiosResponse<GameSession[]> = await apiClient.get('/api/game/public');
      
      // API now consistently returns an array directly
      return {
        data: response.data,
        success: true,
        message: response.data.length === 0 ? 'No games found. Why not create one?' : 'Public games loaded successfully'
      };
    } catch (error: any) {
      return {
        data: [],
        success: false,
        message: error.response?.data?.message || 'Failed to load public games',
        errors: error.response?.data?.errors,
      };
    }
  },

  async getUserGames(): Promise<ApiResponse<GameSession[]>> {
    try {
      const response: AxiosResponse<GameSession[]> = await apiClient.get('/api/game/my');
      
      // API now consistently returns an array directly
      return {
        data: response.data,
        success: true,
        message: response.data.length === 0 ? 'You have no games. Create a new game to get started!' : 'Your games loaded successfully'
      };
    } catch (error: any) {
      return {
        data: [],
        success: false,
        message: error.response?.data?.message || 'Failed to load user games',
        errors: error.response?.data?.errors,
      };
    }
  },

  async getGameByCode(gameCode: string): Promise<ApiResponse<GameSession>> {
    try {
      // Call the backend API endpoint to get game by code
      const response = await apiClient.get(`/api/game/${gameCode}`);
      
      if (!response.data) {
        throw new Error('Game not found');
      }
      
      // The API response might directly return a GameSession
      const responseData = response.data;
      
      // Get the host name from the response or find it in the players array
      console.log('Game response data:', responseData);
      
      // First try to use the hostName directly from the response
      let hostName = responseData.hostName;
      
      // Default if we still couldn't find a host name
      if (!hostName) {
        hostName = 'Unknown Host';
      }
      
      // Create a Game object with available data or defaults
      const game: GameSession = {
        id: responseData.id || '',
        title: responseData.title || 'Game Session',
        description: responseData.description || '',
        status: responseData.status  ,
        statusName: responseData.statusName,
        gameCode: gameCode,
        gameType: responseData.gameType,
        totalQuestionCount: responseData.totalQuestionCount,
        currentQuestionIndex: responseData.currentQuestionIndex,
        gameSpaceId: responseData.gameSpaceId,
        hostUserId: responseData.hostUserId,
        isPublic: responseData.isPublic || false,
        isActive: responseData.isActive,
        maxPlayers: responseData.maxPlayers || 10,
        currentPlayerCount: responseData.players?.length || 0,        
        questionCollectionId: responseData.questionCollectionId || '',
        questionCollectionTitle: responseData.questionCollectionTitle || 'Game Collection',
        startTime: responseData.startedAt || responseData.createdAt,
        endTime: responseData.endedAt,
        createdAt: responseData.createdAt || new Date().toISOString(),
        allowExternalHost: responseData.allowExternalHost || false, 
        players: responseData.players || [], 
        leaderboard: responseData.leaderboard || [], 
        hostName: responseData.hostName,
        hostId: responseData.hostId
      };
      
      // Cache the game for future use
      sessionStorage.setItem(`game_${gameCode}`, JSON.stringify(game));
      
      return {
        data: game,
        success: true,
        message: 'Game found'
      };
    } catch (error: any) {
      console.error('Error fetching game by code:', error);
      
      // Check if we have a cached version as fallback
      const cachedGame = sessionStorage.getItem(`game_${gameCode}`);
      if (cachedGame) {
        try {
          const game = JSON.parse(cachedGame) as GameSession;
          return {
            data: game,
            success: true,
            message: 'Game found (cached)'
          };
        } catch (e) {
          // Invalid cache
          sessionStorage.removeItem(`game_${gameCode}`);
        }
      }
      
      return  {
        data: {} as GameSession, // Return empty object instead of null
        success: false,
        message: error.response?.data?.message || 'Failed to find game with the provided code',
        errors: error.response?.data?.errors
      };
    }
  },
  
  // // Legacy method kept for reference
  // async _getGameByCodeLegacy(gameCode: string): ApiResponse<Game> {
  //   // This is the old implementation that created a dummy game object
  //   const game: Game = {
  //       id: `temp-${gameCode}`,
  //       title: `Game ${gameCode}`,
  //       description: 'Game details will be updated when connected',
  //       gameCode: gameCode,
  //       isPublic: false,
  //       isActive: true,
  //       maxPlayers: 10,
  //       currentPlayers: 0,
  //       status: 'WaitingForPlayers',
  //       questionCollectionId: '',
  //       questionCollectionTitle: 'Loading...',
  //       hostName: '',
  //       createdAt: new Date().toISOString(),
  //       allowExternalHost: false
  //     };
      
  //     // Store in session storage for future use
  //     sessionStorage.setItem(`game_${gameCode}`, JSON.stringify(game));
      
  //     return {
  //       data: game,
  //       success: true,
  //       message: 'Game found (placeholder)'
  //     };
  //   },

  joinGame: async (gameCode: string, playerName?: string): Promise<ApiResponse<JoinGameResponse>> => {
    try {
      // Send playerName as a query parameter instead of in the request body
      const url = playerName ? 
        `/api/game/${gameCode}/join?playerName=${encodeURIComponent(playerName)}` : 
        `/api/game/${gameCode}/join`;
      
      const response: AxiosResponse<JoinGameResponse> = await apiClient.post(url, {});
      return {
        data: response.data,
        success: true,
        message: 'Successfully joined game'
      };
    } catch (error: any) {
      return {
        data: {} as JoinGameResponse,
        success: false,
        message: error.response?.data?.message || 'Failed to join game',
        errors: error.response?.data?.errors,
      };
    }
  },
  
  joinGameSpace: async (gameCode: string, playerName?: string): Promise<ApiResponse<GameSpaceInfo>> => {
    try {
      // Create request payload with player name
      const payload = playerName ? { playerName } : {};
      
      const response: AxiosResponse<GameSpaceInfo> = await apiClient.post(`/api/game/space/${gameCode}/join`, payload);
      return {
        data: response.data,
        success: true,
        message: 'Successfully joined GameSpace'
      };
    } catch (error: any) {
      return {
        data: {} as GameSpaceInfo,
        success: false,
        message: error.response?.data?.message || 'Failed to join GameSpace',
        errors: error.response?.data?.errors,
      };
    }
  },

  leaveGame: async (gameCode: string): Promise<ApiResponse<void>> => {
    try {
      await apiClient.post(`/api/game/${gameCode}/leave`);
      return {
        data: undefined,
        success: true,
        message: 'Successfully left game'
      };
    } catch (error: any) {
      return {
        data: undefined,
        success: false,
        message: error.response?.data?.message || 'Failed to leave game',
        errors: error.response?.data?.errors,
      };
    }
  },

  submitAnswer: async (answer: Answer): Promise<ApiResponse<void>> => {
    try {
      // The server endpoint requires sessionId in the URL path
      if (!answer.sessionId) {
        console.error('Cannot submit answer: sessionId is required');
        return {
          data: undefined,
          success: false,
          message: 'Session ID is required to submit an answer'
        };
      }
      
      // Use the correct endpoint format: /api/game/sessions/{sessionId}/answer
      await apiClient.post(`/api/game/sessions/${answer.sessionId}/answer`, answer);
      
      return {
        data: undefined,
        success: true,
        message: 'Answer submitted successfully'
      };
    } catch (error: any) {
      console.error('Error submitting answer:', error);
      return {
        data: undefined,
        success: false,
        message: error.response?.data?.message || 'Failed to submit answer',
        errors: error.response?.data?.errors,
      };
    }
  },

  startGame: async (sessionId: string): Promise<ApiResponse<void>> => {
    try {
      await apiClient.post(`/api/game/sessions/${sessionId}/start`);
      return {
        data: undefined,
        success: true,
        message: 'Game started successfully'
      };
    } catch (error: any) {
      return {
        data: undefined,
        success: false,
        message: error.response?.data?.message || 'Failed to start game',
        errors: error.response?.data?.errors,
      };
    }
  },

  createGame: async (gameSessionData: {
    title: string;
    description?: string;
    isPublic: boolean;
    maxPlayers: number;
    questionCollectionId: string;
    allowExternalHost?: boolean;
    questionCount?: number;
    gameSpaceId?: string;
    status?: number;
  }): Promise<ApiResponse<GameSession>> => {
    try {
      // Set default status to 1 (WaitingForPlayers) if not provided
      const gameDataWithStatus = {
        ...gameSessionData,
        status: gameSessionData.status ?? 1  // Default to WaitingForPlayers (1) if not specified
      };
      
      console.log('[GameService] Creating game with data:', gameDataWithStatus);
      const response: AxiosResponse<GameSession> = await apiClient.post('/api/game', gameDataWithStatus);
      return {
        data: response.data,
        success: true,
        message: 'Game created successfully'
      };
    } catch (error: any) {
      return {
        data: {} as GameSession,
        success: false,
        message: error.response?.data?.message || 'Failed to create game',
        errors: error.response?.data?.errors,
      };
    }
  },

  getHostInfo: async (): Promise<ApiResponse<HostInfo>> => {
    try {
      const response: AxiosResponse<HostInfo> = await apiClient.get('/api/game/host/info');
      return {
        data: response.data,
        success: true,
        message: 'Host information retrieved successfully'
      };
    } catch (error: any) {
      return {
        data: {} as HostInfo,
        success: false,
        message: error.response?.data?.message || 'Failed to get host information',
        errors: error.response?.data?.errors,
      };
    }
  },

  upgradeToPro: async (request: UpgradeToProRequest): Promise<ApiResponse<HostInfo>> => {
    try {
      const response: AxiosResponse<HostInfo> = await apiClient.post('/api/game/host/upgrade-to-pro', request);
      return {
        data: response.data,
        success: true,
        message: 'Successfully upgraded to Pro'
      };
    } catch (error: any) {
      return {
        data: {} as HostInfo,
        success: false,
        message: error.response?.data?.message || 'Failed to upgrade to Pro',
        errors: error.response?.data?.errors,
      };
    }
  },

  updateGameSpace: async (gameSpaceId: string, gameSpaceData: {
    name?: string;
    description?: string;
    isPublic?: boolean;
    maxPlayers?: number;
    allowExternalHost?: boolean;
  }): Promise<ApiResponse<GameSpaceInfo>> => {
    try {
      const response: AxiosResponse<GameSpaceInfo> = await apiClient.put(`/api/game/space/${gameSpaceId}`, gameSpaceData);
      return {
        data: response.data,
        success: true,
        message: 'Game space updated successfully'
      };
    } catch (error: any) {
      return {
        data: {} as GameSpaceInfo,
        success: false,
        message: error.response?.data?.message || 'Failed to update game space',
        errors: error.response?.data?.errors,
      };
    }
  },
  
  addTokens: async (request: AddTokensRequest): Promise<ApiResponse<HostInfo>> => {
    try {
      const response: AxiosResponse<HostInfo> = await apiClient.post('/api/game/host/add-tokens', request);
      return {
        data: response.data,
        success: true,
        message: `Successfully added ${request.tokenAmount} tokens`
      };
    } catch (error: any) {
      return {
        data: {} as HostInfo,
        success: false,
        message: error.response?.data?.message || 'Failed to add tokens',
        errors: error.response?.data?.errors,
      };
    }
  },
  
  getQuestion: async (questionId: number): Promise<ApiResponse<Question>> => {
    try {
      const response = await apiClient.get(`/api/questions/${questionId}`);
      return { 
        success: true, 
        data: response.data,
        message: 'Question retrieved successfully'
      };
    } catch (error: any) {
      console.error(`Error getting question ${questionId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve question',
        errors: error.response?.data?.errors || [],
        data: {} as Question
      };
    }
  },

  getCurrentQuestion: async (gameSessionId: number): Promise<ApiResponse<Question>> => {
    try {
      console.log(`[GameService] Fetching current question for session ${gameSessionId}`);
      const response = await apiClient.get(`/api/game/sessions/${gameSessionId}/current-question`);
      console.log(`[GameService] Current question response:`, response.data);
      return { 
        success: true, 
        data: response.data,
        message: 'Current question retrieved successfully'
      };
    } catch (error: any) {
      console.error(`[GameService] Error getting current question for session ${gameSessionId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve current question',
        errors: error.response?.data?.errors || [],
        data: {} as Question
      };
    }
  },

  getSessionLeaderboard: async (gameSessionId: number): Promise<ApiResponse<LeaderboardEntry[]>> => {
    try {
      const response = await apiClient.get(`/api/game/sessions/${gameSessionId}/leaderboard`);
      return { 
        success: true, 
        data: response.data,
        message: 'Leaderboard retrieved successfully'
      };
    } catch (error: any) {
      console.error(`Error getting leaderboard for session ${gameSessionId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve leaderboard',
        errors: error.response?.data?.errors || [],
        data: [] as LeaderboardEntry[]
      };
    }
  },

  startNextQuestion: async (gameSessionId: number): Promise<ApiResponse<void>> => {
    try {
      console.log(`[GameService] Starting next question for session ${gameSessionId}`);
      const response = await apiClient.post(`/api/game/sessions/${gameSessionId}/next-question`);
      return { 
        success: true, 
        message: 'Next question started successfully',
        data: undefined
      };
    } catch (error: any) {
      console.error(`[GameService] Error starting next question for session ${gameSessionId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to start next question',
        errors: error.response?.data?.errors || [],
        data: undefined
      };
    }
  },

  /**
   * Become a guest host for a game session when external hosting is allowed
   * @param gameSessionId - ID of the game session
   * @returns Response indicating success or failure
   */
  becomeGuestHost: async (gameSessionId: number): Promise<ApiResponse<any>> => {
    try {
      console.log(`[GameService] Attempting to become guest host for session ${gameSessionId}`);
      const response = await apiClient.post(`/api/game/sessions/${gameSessionId}/become-guest-host`);
      return {
        success: true,
        data: response.data,
        message: 'Successfully became a guest host'
      };
    } catch (error: any) {
      console.error(`[GameService] Error becoming guest host for session ${gameSessionId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to become guest host',
        errors: error.response?.data?.errors || [],
        data: undefined
      };
    }
  },
  
  /**
   * Remove a player from a game session
   * @param gameSessionId - ID of the game session
   * @param userId - ID of the player to remove
   * @returns Response indicating success or failure
   */
  removePlayerFromGame: async (gameSessionId: number | string, userId: string): Promise<ApiResponse<any>> => {
    try {
      console.log(`[GameService] Removing player ${userId} from session ${gameSessionId}`);
      const response = await apiClient.post(`/api/game/sessions/${gameSessionId}/remove-player`, { userId });
      return {
        success: true,
        data: response.data,
        message: 'Successfully removed player from game'
      };
    } catch (error: any) {
      console.error(`[GameService] Error removing player ${userId} from session ${gameSessionId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to remove player from game',
        errors: error.response?.data?.errors || [],
        data: undefined
      };
    }
  },
  
  /**
   * Update a player's connection status in the database
   * @param gameSessionId - ID of the game session
   * @param userId - ID of the player
   * @param isConnected - Whether the player is connected
   * @returns Response indicating success or failure
   */
  updatePlayerConnectionStatus: async (gameSessionId: number | string, userId: string, isConnected: boolean): Promise<ApiResponse<any>> => {
    try {
      console.log(`[GameService] Updating player ${userId} connection status to ${isConnected ? 'connected' : 'disconnected'} in session ${gameSessionId}`);
      const response = await apiClient.post(`/api/game/sessions/${gameSessionId}/update-player-connection`, { 
        userId,
        isConnected
      });
      return {
        success: true,
        data: response.data,
        message: `Successfully updated player connection status to ${isConnected ? 'connected' : 'disconnected'}`
      };
    } catch (error: any) {
      console.error(`[GameService] Error updating player ${userId} connection status in session ${gameSessionId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update player connection status',
        errors: error.response?.data?.errors || [],
        data: undefined
      };
    }
  },
};

export default gameService;
